import { useCurrentWorkspace } from '@gripp/shared-logic';
import { SlideAction, SlideLayout, Spacing, ViewMode } from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import ActivityEditLabels from '../../activity/components/activity-edit-label';
import { NewIssueBody } from '../../issues';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetNewIssue = () => {
  const { assetId = '', issueId: contextIssueId } = useParams();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const resetLabelsRef = useRef<(() => void) | undefined>(undefined);

  const handleIssueCreated = (newIssueId: string) => {
    if (contextIssueId) {
      // Activities context — navigate to the new issue in activities.
      navigate(`/activities/${newIssueId}`);
    } else {
      // Assets context — navigate to the new issue within the asset.
      navigate(`${basePath}/activity/${newIssueId}`);
    }
  };

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: (
        <AssetCard assetId={assetId} />
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    // activities/:issueId/asset/:assetId/activity/new → 2 up → /activities/:issueId/asset/:assetId
    // assets/:assetId/activity/new                   → 2 up → /assets/:assetId
    onBackButtonPress: () => navigate('../../../..', { relative: 'path' }),
  };

  const rightColumn = {
    title: 'issue.new.addIt',
    mainComponent: (
      <NewIssueBody
        assetId={assetId}
        workspaceId={currentWorkspace?.id}
        isOpened={true}
        modalType="sheet"
        onComplete={() => navigate('../..', { relative: 'path' })}
        onCancel={() => navigate('../..', { relative: 'path' })}
        onIssueCreated={handleIssueCreated}
        selectedLabels={selectedLabels}
        onLabelsChange={setSelectedLabels}
        onSelectorEdit={() => setIsLabelsPanelOpen(true)}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate('../..', { relative: 'path' }),
    disableScroll: true,
  };

  const thirdColumn = isLabelsPanelOpen
    ? {
        title: 'asset.groups',
        mainComponent: (
          <ActivityEditLabels
            selectedLabels={selectedLabels ?? []}
            onLabelsChange={setSelectedLabels}
            resetRef={resetLabelsRef}
          />
        ),
        headerComponent: (
          <View style={styles.headerResetButton}>
            <HeaderResetButton onReset={() => resetLabelsRef.current?.()} />
          </View>
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsLabelsPanelOpen(false),
      }
    : undefined;

  return (
    <AssetRouteProvider value={{
      basePath: contextIssueId ? `/activities/${contextIssueId}/asset/${assetId}` : `/assets/${assetId}`,
      listPath: contextIssueId ? '/activities' : '/assets',
      isWorkspaceMode: false,
      issueId: contextIssueId,
    }}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        viewMode={ViewMode.DUAL}
      />
    </AssetRouteProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  headerResetButton: {
    marginRight: 8,
  },
});
